import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { api_url } from "@/utils/apiConfig";
import BarCandles from "@/components/BarCandles";
import BarChart2 from "@/components/BarChart2";
import BarChart3 from "@/components/BarChart3";
import {
  generateValuesSPX,
  assetsLineBgColor,
  buildMergedAssetOptions,
  findCustomAsset,
  getAssetTypeCode,
} from "@/utils/helper";
import { CatListDropdown } from "@/components/FileInput";
import {
  OtherDropdown,
  percentileOptions0To100,
  percentileOptions5To95,
  generateMix,
  generateMixReturnsWealth,
  binMapping,
  generateCartoonsReturns,
  isEmpty,
} from "@/utils/helper";
import { generateUniqueId } from "@/utils/FileInputUtils";

import { Slider } from "@/components/FileInput";
import BarChart4 from "@/components/BarChart4";
import Loader from "@/components/Loader";
import Chart5 from "@/modules/Chart5";
import CustomSlider from "@/components/CustomSlider";
import AllocCEAplot from "../AllocCEAplot";
import BtnGroup from "../shared/BtnGroup";
import { SvgLock, SvgUnlock } from "@/utils/svgs";
import RangeSelector from "../singleAsset/sections/RangeSelectror";
import clsx from "clsx";
import useCustomAssets from "@/hooks/useCustomAssets";
import {
  callSafeHavenScorecard,
  buildScorecardBatchRequest,
} from "@/services/api/safeHavenScorecard";
import { applyMultiScorecardResponse } from "@/services/api/applyMultiScorecardResponse";
import {
  PARITY_SEED,
} from "@/dev/scorecardParityTestkit";
import { resolveCartoonConfig } from "@/services/api/applyScorecardResponse";
import { useScorecardInputs } from "@/hooks/useScorecardInputs";
import {
  portfolioRequestFailure,
  portfolioRequestStart,
  portfolioRequestSuccess,
} from "@/store/slices/portfolioSlice";

const selectedValueFlip = 25;
const selectedValuePath = 1000;
const timeFrame = "a";
const MultiAsset = ({
  usePyScorecard = false,
  multiAssetToggle,
  handleMultiAssetToggle,
  triggerRerender,
}) => {
  const dispatch = useDispatch();
  const {
    portfolio,
    patch,
    setFromYear,
    setToYear,
    setYearsList,
    setSelectedType,
    setRegimeCount,
    setRegimeSelectors,
    setLock,
    setPin,
    setAllocLock,
    setAssets,
    setActiveAsset,
    setSecondAssets: setSecondAsset,
    setSecondAssetNames: setSecondAssetName,
    setInsuranceSliderValues: setInsuranceSliderValue,
    setAllocByAsset: setAlloc,
    setPercentile1Dropdown,
    setPercentile2Dropdown,
  } = useScorecardInputs();

  const {
    fromYear,
    toYear,
    yearsList,
    selectedType,
    firstAsset,
    firstAssetName,
    secondAssets: secondAsset,
    secondAssetNames: secondAssetName,
    insuranceSliderValues: insuranceSliderValue,
    regimeCount,
    regimeSelectors,
    allocByAsset: alloc,
    percentile1Dropdown,
    percentile2Dropdown,
    lock,
    allocLock,
    pin,
    assets,
    activeAsset,
  } = portfolio;

  // fetch annual data
  let insuranceTypes = ["INSUR[10-to-1]", "INSUR[12-to-1]", "INSUR[15-to-1]"];

  const [firstAssetSeries, setFirstAssetSeries] = useState({
    firstAssetValues: [],
    firstAssetReturns: [],
  });
  const { firstAssetValues, firstAssetReturns } = firstAssetSeries;

  const setFirstAssetData = useCallback(
    (updater) => {
      setFirstAssetSeries((series) => {
        const prev = {
          firstAsset,
          firstAssetName,
          firstAssetValues: series.firstAssetValues,
          firstAssetReturns: series.firstAssetReturns,
        };
        const next =
          typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
        if (
          next.firstAsset !== firstAsset ||
          next.firstAssetName !== firstAssetName
        ) {
          patch({
            firstAsset: next.firstAsset,
            firstAssetName: next.firstAssetName,
          });
        }
        return {
          firstAssetValues: next.firstAssetValues ?? [],
          firstAssetReturns: next.firstAssetReturns ?? [],
        };
      });
    },
    [firstAsset, firstAssetName, patch]
  );

  const [minMaxAvgArray, setMinMaxAvgArray] = useState({
    minArray: [],
    maxArray: [],
    averageArray: [],
  });
  const { minArray, maxArray, averageArray } = minMaxAvgArray;
  const [minMaxAvgArrayNoCrash, setMinMaxAvgArrayNoCrash] = useState({
    minArrayNocrash: [],
    maxArrayNocrash: [],
    averageArrayNocrash: [],
  });
  const { minArrayNocrash, maxArrayNocrash, averageArrayNocrash } =
    minMaxAvgArrayNoCrash;

  const [minAssetArray, setMinAssetArray] = useState({ 1: [] });
  const [maxAssetArray, setMaxAssetArray] = useState({ 1: [] });
  const [averageAssetArray, setAverageAssetArray] = useState({ 1: [] });

  const [minAssetArrayNocrash, setMinAssetArrayNocrash] = useState({});
  const [maxAssetArrayNocrash, setMaxAssetArrayNocrash] = useState({});
  const [averageAssetArrayNocrash, setAverageAssetArrayNocrash] = useState({});

  const [minAssetReshuffleArray, setMinAssetReshuffleArray] = useState({});
  const [maxAssetReshuffleArray, setMaxAssetReshuffleArray] = useState({});
  const [averageAssetReshuffleArray, setAverageAssetReshuffleArray] = useState(
    {}
  );

  const [am, setAm] = useState(0);
  const [amNocrash, setAmNocrash] = useState(0);

  const [amMean, setAmMean] = useState({});

  const [amMeanNocrash, setAmMeanNocrash] = useState(0);
  const [amMeanReshuffle, setAmMeanReshuffle] = useState(0);
  const [gm, setGm] = useState(0);
  const [gmNocrash, setGmNocrash] = useState(0);
  const [yearMatrixData, setYearMatrixData] = useState([]);
  const [yearMatrixDataNocrash, setYearMatrixDataNocrash] = useState([]);
  const userAssets = useCustomAssets();
  const { user } = useSelector((state) => state.userDetails);
  const scorecardUserId = user?.id != null ? Number(user.id) : undefined;
  const secondAssetDropdown = useMemo(
    () => buildMergedAssetOptions(OtherDropdown, userAssets, true),
    [userAssets]
  );

  const normalizeAnnualReturns = useCallback((annualReturns) => {
    if (!annualReturns) return [];

    if (Array.isArray(annualReturns)) {
      return annualReturns
        .map((item) => {
          if (Array.isArray(item) && item.length >= 2) {
            return [Number(item[0]), Number(item[1])];
          }
          if (item && typeof item === "object") {
            const year = Number(item.year ?? item.Year ?? item.y);
            const value = Number(
              item.annual_ret ??
                item.annualRet ??
                item.return ??
                item.returns ??
                item.value ??
                item.ret
            );
            return Number.isFinite(year) && Number.isFinite(value)
              ? [year, value]
              : null;
          }
          return null;
        })
        .filter(Boolean)
        .sort((a, b) => a[0] - b[0]);
    }

    if (typeof annualReturns === "object") {
      return Object.entries(annualReturns)
        .map(([year, value]) => [Number(year), Number(value)])
        .filter(([year, value]) => Number.isFinite(year) && Number.isFinite(value))
        .sort((a, b) => a[0] - b[0]);
    }

    return [];
  }, []);

  const resolveAssetSource = useCallback(
    (assetValue) => {
      const customAsset = findCustomAsset(assetValue, userAssets);
      if (!customAsset) {
        return {
          assetKind: "standard",
          apiType: assetValue,
          annualReturns: [],
        };
      }

      const baseAsset =
        customAsset.baseAsset ||
        customAsset.base_asset ||
        customAsset.baseasset ||
        customAsset.base ||
        customAsset.underlyingAsset ||
        customAsset.underlying_asset ||
        customAsset.benchmark ||
        "";
      const mappedBaseAssetType = getAssetTypeCode(baseAsset);

      const annualReturns = normalizeAnnualReturns(
        customAsset.annual_ret ||
          customAsset.annualRet ||
          customAsset.annual_returns ||
          customAsset.annualReturns
      );

      const assetType = String(
        customAsset.assetType ||
          customAsset.type ||
          customAsset.sourceType ||
          customAsset.asset_source ||
          ""
      ).toLowerCase();

      const isHistorical =
        assetType.includes("hist") || (annualReturns.length > 0 && !baseAsset);

      if (isHistorical) {
        return {
          assetKind: "historical",
          apiType: null,
          annualReturns,
        };
      }

      return {
        assetKind: "custom",
        apiType: mappedBaseAssetType || baseAsset || assetValue,
        annualReturns,
      };
    },
    [userAssets, normalizeAnnualReturns]
  );

  const [percentile2DataMaxAllocation, setPercentile2DataMaxAllocation] =
    useState({});
  const [secondAssetValues, setSecondAssetValues] = useState({});

  //cpi
  const [cpiValues, setCpiValues] = useState([]);
  const [cpiReturns, setCpiReturns] = useState([]);

  const handleSecondAsset = async (event, id) => {
    setActiveAsset(id);
    let selectedOptionText =
      event.target.options[event.target.selectedIndex].text;
    let value = event.target.value;

    userOverrodeAllocRef.current[id] = false;
    setSecondAsset((prevValue) => ({ ...prevValue, [id]: value }));
    setSecondAssetName((prevValue) => ({
      ...prevValue,
      [id]: selectedOptionText,
    }));

    switch (value) {
      case "Store-of-Value [CPI+2%]":
        if (usePyScorecard) break;
        setSecondAssetValues((prevValue) => ({
          ...prevValue,
          [id]: [
            ...generateCartoonsReturns(
              fromYear,
              toYear,
              "Store-of-Value CPI",
              [2, 2, 2, 2],
              cpiValues,
              firstAssetValues,
              bins
            ),
          ],
        }));
        break;
      case "Store-of-Value [7%]":
        if (usePyScorecard) break;
        setSecondAssetValues((prevValue) => ({
          ...prevValue,
          [id]: [
            ...generateCartoonsReturns(
              fromYear,
              toYear,
              "Store-of-Value",
              [7, 7, 7, 7],
              cpiValues,
              firstAssetValues,
              bins
            ),
          ],
        }));
        break;
      case "Alpha [20,7.5,5,5]":
        if (usePyScorecard) break;
        setSecondAssetValues((prevValue) => ({
          ...prevValue,
          [id]: [
            ...generateCartoonsReturns(
              fromYear,
              toYear,
              "Alpha",
              [20, 7.5, 5, 5],
              cpiValues,
              firstAssetValues,
              bins
            ),
          ],
        }));
        break;
      case "Alpha [15,10,2.5,2.5]":
        if (usePyScorecard) break;
        setSecondAssetValues((prevValue) => ({
          ...prevValue,
          [id]: [
            ...generateCartoonsReturns(
              fromYear,
              toYear,
              "Alpha",
              [15, 10, 2.5, 2.5],
              cpiValues,
              firstAssetValues,
              bins
            ),
          ],
        }));
        break;
      case "INSUR[10-to-1]":
        if (usePyScorecard) break;
        setSecondAssetValues((prevValue) => ({
          ...prevValue,
          [id]: [
            ...generateCartoonsReturns(
              fromYear,
              toYear,
              "Insurance",
              [1000, -100, -100, -100],
              cpiValues,
              firstAssetValues,
              bins
            ),
          ],
        }));
        break;
      case "INSUR[12-to-1]":
        if (usePyScorecard) break;
        setSecondAssetValues((prevValue) => ({
          ...prevValue,
          [id]: [
            ...generateCartoonsReturns(
              fromYear,
              toYear,
              "Insurance",
              [1200, -100, -100, -100],
              cpiValues,
              firstAssetValues,
              bins
            ),
          ],
        }));
        break;
      case "INSUR[15-to-1]":
        if (usePyScorecard) break;
        setSecondAssetValues((prevValue) => ({
          ...prevValue,
          [id]: [
            ...generateCartoonsReturns(
              fromYear,
              toYear,
              "Insurance",
              [1500, -100, -100, -100],
              cpiValues,
              firstAssetValues,
              bins
            ),
          ],
        }));
        break;

      default:
        const { assetKind, apiType, annualReturns } = resolveAssetSource(value);
        if (assetKind === "historical") {
          if (!usePyScorecard) {
            setSecondAssetValues((prevValue) => ({
              ...prevValue,
              [id]: annualReturns,
            }));
          }
          break;
        }
        if (usePyScorecard) break;
        const options = {
          fromYear: 1901,
          toYear: 2025,
          type: apiType,
          frequency: timeFrame,
        };
        const assetRetrunValues = await callApiBasic(options);
        setSecondAssetValues((prevValue) => ({
          ...prevValue,
          [id]: assetRetrunValues,
        }));
        break;
    }
  };

  const handleSetPercentile2DataMaxAllocation = (value, id) => {
    setPercentile2DataMaxAllocation((prevValue) => ({
      ...prevValue,
      [id]: value,
    }));
  };

  const [dataBar, setDataBar] = useState({
    labels: ["data1", "data2"],
    datasets: [
      {
        label: ["data1", "data2"],
        data: [0, 0],
        backgroundColor: "rgba(255, 99, 132, 0.5)",
      },
    ],
  });

  const [regimeBar, setRegimeBar] = useState({
    labels: ["Loading", "Loading", "Loading"],
    datasets: [],
  });

  const [regimeBarForNocrash, setRegimeBarForNocrash] = useState({
    labels: ["Loading", "Loading", "Loading"],
    datasets: [],
  });

  const [mix, setMix] = useState([]);

  const [mixMinAssetReshuffleArray, setMixMinAssetReshuffleArray] = useState(
    {}
  );
  const [mixMaxAssetReshuffleArray, setMixMaxAssetReshuffleArray] = useState(
    {}
  );
  const [mixAvgAssetReshuffleArray, setMixAvgAssetReshuffleArray] = useState(
    {}
  );

  const [mixMinAssetArray, setMixMinAssetArray] = useState({});
  const [mixMaxAssetArray, setMixMaxAssetArray] = useState({});
  const [mixAvgAssetArray, setMixAvgAssetArray] = useState({});
  const [mixAM, setMixAM] = useState({});
  const [mixGM, setMixGM] = useState({});

  const [mixAMReshuffle, setMixAMReshuffle] = useState({});
  const [mixGMReshuffle, setMixGMReshuffle] = useState({});

  //for normal or default
  const [baseAssetMatrixRetruns, setBaseAssetMatrixReturns] = useState([]);

  const [otherAssetMatrixRetruns, setOtherAssetMatrixReturns] = useState({});

  //for reshuffle

  const [
    otherAssetMatrixReshuffleReturns,
    setOtherAssetMatrixReshuffleReturns,
  ] = useState({});

  //for nocrash
  const [baseAssetMatrixReturnsNoCrash, setBaseAssetMatrixReturnsNoCrash] =
    useState([]);
  // const [otherAssetMatrixReturnsNoCrash, setOtherAssetMatrixReturnsNoCrash] = useState([]);
  const [otherAssetMatrixReturnsNoCrash, setOtherAssetMatrixReturnsNoCrash] =
    useState({});

  const [minMixNoCrash, setMinMixNoCrash] = useState({});
  const [maxMixNoCrash, setMaxMixNoCrash] = useState({});
  const [avgMixNoCrash, setAvgMixNoCrash] = useState({});
  const [amMixNoCrash, setAmMixNoCrash] = useState({});
  const [gmMixNoCrash, setGmMixNoCrash] = useState({});

  // Allocation mix

  //loader
  const [loading, setLoading] = useState(true);

  //for cartoons generation
  const [bins, setBins] = useState([]);
  const [chart5ByMode, setChart5ByMode] = useState({});
  /** Columns where the user moved the alloc slider — skip recommendedAlloc snap. */
  const userOverrodeAllocRef = useRef({});
  /** User released alloc slider — dedicated effect should refetch (snap must not). */
  const pendingUserAllocFetchRef = useRef(false);
  const [regimeCommitNonce, setRegimeCommitNonce] = useState(0);
  const [allocCommitNonce, setAllocCommitNonce] = useState(0);
  const allocRef = useRef(alloc);
  allocRef.current = alloc;

  const buildDefaultRegimeSelectors = (dataLength, count) => {
    if (dataLength < 2) return [1, dataLength || 1];
    const step = Math.round(dataLength / (count + 1));
    const buffer = [1];
    for (let i = 1; i <= count; i++) buffer.push(step * i);
    buffer.push(dataLength);
    return buffer;
  };

  const allocCEAplotRef = useRef(null);

  const isInsurance = (assetName) => {
    return insuranceTypes.some((type) => assetName.includes(type));
  };

  const pyMultiApplySetters = {
    setDataBar,
    setRegimeBar,
    setRegimeBarForNocrash,
    setYearsList,
    setBins,
    setMinMaxAvgArray,
    setMinMaxAvgArrayNoCrash,
    setMinAssetArray,
    setMaxAssetArray,
    setAverageAssetArray,
    setMinAssetArrayNocrash,
    setMaxAssetArrayNocrash,
    setAverageAssetArrayNocrash,
    setMinAssetReshuffleArray,
    setMaxAssetReshuffleArray,
    setAverageAssetReshuffleArray,
    setMixMinAssetArray,
    setMixMaxAssetArray,
    setMixAvgAssetArray,
    setMixMinAssetReshuffleArray,
    setMixMaxAssetReshuffleArray,
    setMixAvgAssetReshuffleArray,
    setMinMixNoCrash,
    setMaxMixNoCrash,
    setAvgMixNoCrash,
    setAm,
    setAmNocrash,
    setGm,
    setGmNocrash,
    setAmMean,
    setAmMeanNocrash,
    setAmMeanReshuffle,
    setMixAM,
    setMixGM,
    setMixAMReshuffle,
    setMixGMReshuffle,
    setAmMixNoCrash,
    setGmMixNoCrash,
    setPercentile2DataMaxAllocation,
    setChart5ByMode,
    setLoading,
  };

  const buildPyBatchPayload = (allocByColumn) => {
    const selectorsValid =
      Array.isArray(regimeSelectors) && regimeSelectors.length === regimeCount + 2;
    return buildScorecardBatchRequest({
      shared: {
        baseAsset: firstAsset,
        fromYear,
        toYear,
        regimeCount,
        regimeSelectors: selectorsValid ? regimeSelectors : undefined,
        mode: selectedType,
        pathCount: selectedValuePath,
        horizon: selectedValueFlip,
        lockAlloc: allocLock,
        seed: PARITY_SEED,
        userId: scorecardUserId,
      },
      columns: assets.map((id) => ({
        columnId: id,
        otherAsset: secondAsset[id],
        firstAssetAlloc: allocByColumn[id]?.firstAssetAlloc,
        secondAssetAlloc: allocByColumn[id]?.secondAssetAlloc,
        percentile1: percentile1Dropdown[id],
        percentile2: percentile2Dropdown[id],
        cartoon: resolveCartoonConfig(secondAsset[id]),
      })),
    });
  };

  useEffect(() => {
    if (usePyScorecard) return;
    callAPI(
      {
        fromYear: 1901,
        toYear: 2025,
        type: firstAsset,
        frequency: timeFrame,
      },
      (values) =>
        setFirstAssetData((prev) => ({ ...prev, firstAssetValues: values })),
      (returns) =>
        setFirstAssetData((prev) => ({ ...prev, firstAssetReturns: returns }))
    );

    const options = {
      fromYear: 1901,
      toYear: 2025,
      type: secondAsset[1],
      frequency: timeFrame,
    };
    intitialSecondAssetValues(options, 1);

    callAPI(
      {
        fromYear: 1901,
        toYear: 2025,
        type: "cpi",
        frequency: "a",
      },
      setCpiValues,
      setCpiReturns
    );
  }, [usePyScorecard]);

  useEffect(() => {
    if (!usePyScorecard) return undefined;

    setLoading(true);
    let cancelled = false;

    const timer = setTimeout(async () => {
      try {
        const payload = buildPyBatchPayload(allocRef.current);
        dispatch(portfolioRequestStart(payload));
        const result = await callSafeHavenScorecard(payload);
        if (cancelled) return;
        dispatch(
          portfolioRequestSuccess({ response: result, requestPayload: payload })
        );
        applyMultiScorecardResponse(result, pyMultiApplySetters, {
          mode: selectedType,
        });
      } catch (error) {
        console.error("safeHavenScorecard batch API failed", error);
        if (!cancelled) {
          dispatch(portfolioRequestFailure(error));
          setLoading(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    usePyScorecard,
    firstAsset,
    fromYear,
    toYear,
    regimeCount,
    regimeCommitNonce,
    selectedType,
    assets,
    secondAsset,
    percentile1Dropdown,
    percentile2Dropdown,
    allocLock,
    scorecardUserId,
    dispatch,
  ]);

  // Alloc-only refetch when the user releases a slider (not recommendedAlloc snap).
  useEffect(() => {
    if (!usePyScorecard) return undefined;
    if (!pendingUserAllocFetchRef.current) return undefined;
    pendingUserAllocFetchRef.current = false;

    setLoading(true);
    let cancelled = false;

    const timer = setTimeout(async () => {
      try {
        const payload = buildPyBatchPayload(alloc);
        dispatch(portfolioRequestStart(payload));
        const result = await callSafeHavenScorecard(payload);
        if (cancelled) return;
        dispatch(
          portfolioRequestSuccess({ response: result, requestPayload: payload })
        );
        applyMultiScorecardResponse(result, pyMultiApplySetters, {
          mode: selectedType,
        });
      } catch (error) {
        console.error("safeHavenScorecard batch API failed", error);
        if (!cancelled) {
          dispatch(portfolioRequestFailure(error));
          setLoading(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [usePyScorecard, allocCommitNonce, dispatch]);

  const skipRegimeCountRebuildRef = useRef(true);
  useEffect(() => {
    if (!usePyScorecard) return;
    if (skipRegimeCountRebuildRef.current) {
      skipRegimeCountRebuildRef.current = false;
      return;
    }
    const dataLength = toYear - fromYear + 1;
    setRegimeSelectors(buildDefaultRegimeSelectors(dataLength, regimeCount));
  }, [regimeCount, usePyScorecard]);

  useEffect(() => {
    if (usePyScorecard) return;
    let buffer = filterData(
      firstAssetName,
      firstAssetValues,
      fromYear,
      toYear,
      true,
      (returns) =>
        setFirstAssetData((prev) => ({ ...prev, firstAssetReturns: returns })),
      true
    );
    calculateRegimeSelectors(buffer);
    if (fromYear == 1901 && toYear == 2020 && regimeCount == 4) {
      setRegimeSelectors([1, 11, 33, 62, 98, 120]);
    }
    setDataBar(buffer);
  }, [fromYear, toYear, firstAssetValues, regimeCount, usePyScorecard]);

  useEffect(() => {
    if (usePyScorecard) return;
    updateRegime(null, null, true);
  }, [dataBar, usePyScorecard]);

  useEffect(() => {
    if (usePyScorecard) return;
    if (
      (selectedType == "normal" ||
        selectedType == "reshuffle" ||
        selectedType == "nocrash") &&
      loading == false &&
      firstAssetValues.length > 0
    ) {
      let newYearsList = yearsList;
      generateDiceMatrixForOneTwentyFaceDice(newYearsList);
    }
  }, [fromYear, toYear, regimeCount, regimeSelectors, yearsList, loading, selectedType, firstAssetValues, usePyScorecard]);

  useEffect(() => {
    if (usePyScorecard) return;
    if (
      (selectedType == "normal" ||
        selectedType == "reshuffle" ||
        selectedType == "nocrash") &&
      loading == false &&
      firstAssetValues.length > 0
    ) {
      let newYearsList = yearsList;
      generateDiceMatrixForOneTwentyFaceDiceForNoCrash(newYearsList);
    }
  }, [fromYear, toYear, regimeCount, regimeSelectors, yearsList, loading, selectedType, firstAssetValues, usePyScorecard]);

  useEffect(() => {
    if (usePyScorecard) return;
    if (
      activeAsset &&
      (selectedType == "normal" ||
        selectedType == "reshuffle" ||
        selectedType == "nocrash") &&
      loading != true
    ) {
      calSecondAsset(yearMatrixData, yearsList, activeAsset);
    }
  }, [
    fromYear,
    toYear,
    regimeCount,
    regimeSelectors,
    yearsList,
    yearMatrixData,
    loading,
    activeAsset,
    secondAssetValues,
    selectedType,
    usePyScorecard,
  ]);

  useEffect(() => {
    if (usePyScorecard) return;
    if (
      activeAsset &&
      (selectedType == "normal" ||
        selectedType == "reshuffle" ||
        selectedType == "nocrash") &&
      loading != true
    ) {
      calSecondAssetForReshuffle(yearMatrixData, yearsList, activeAsset);
    }
  }, [
    fromYear,
    toYear,
    regimeCount,
    regimeSelectors,
    yearsList,
    yearMatrixData,
    loading,
    activeAsset,
    secondAssetValues,
    selectedType,
    usePyScorecard,
  ]);

  useEffect(() => {
    if (usePyScorecard) return;
    if (
      activeAsset &&
      (selectedType == "normal" ||
        selectedType == "reshuffle" ||
        selectedType == "nocrash")
    ) {
      calSecondAssetForNocrash(yearMatrixDataNocrash, yearsList, activeAsset);
    }
  }, [
    fromYear,
    toYear,
    regimeCount,
    regimeSelectors,
    yearsList,
    yearMatrixDataNocrash,
    activeAsset,
    secondAssetValues,
    selectedType,
    usePyScorecard,
  ]);

  const callAPI = async (options, responseSetter, returnsSetter, id) => {
    try {
      setLoading(true);
      const data = await axios.post(`${api_url}/fetch-data`, options);
      let raw_data = (data && data.data.data) || [];
      responseSetter(raw_data);
      let returns = generateValuesSPX(fromYear, toYear, raw_data);
      returnsSetter(returns);
      setLoading(false);
      return true;
    } catch (error) {
      setLoading(false);
    }
  };

  const calculateRegimeSelectors = (response) => {
    let dataLength = response.datasets[0].data.length;
    let stepSize = Math.round(dataLength / (regimeCount + 1));
    let buffer = [];
    buffer.push(1);
    for (let i = 1; i <= regimeCount; i++) {
      buffer.push(stepSize * i);
    }
    buffer.push(dataLength);
    setRegimeSelectors(buffer);
  };

  const filterData = (
    label,
    raw_data,
    from,
    to,
    calculateReturns = false,
    returnsSetterFxn = null,
    sort = true,
    multiplier = 100
  ) => {
    let response = {
      labels: [],
      datasets: [
        {
          label: label,
          data: [],
          backgroundColor: "rgba(255, 99, 132, 0.5)",
        },
      ],
    };

    raw_data.sort((a, b) => a[1] - b[1]);

    // TODO: setup the if conditions to minimize iteration of the loop
    raw_data.forEach((element) => {
      if (element[0] >= from && element[0] <= to) {
        response.labels.push(element[0]);
        response.datasets[0].data.push(parseFloat(element[1] * multiplier));
      }
    });

    // SORT ASCENDING ORDER.
    if (sort) {
      response.datasets[0].data.sort((a, b) => a - b);
    }

    if (calculateReturns) {
      let returns = generateValuesSPX(from, to, raw_data);
      returnsSetterFxn(returns);
    }

    return response;
  };

  const handleYearChange = ({ min, max }) => {
    const from = parseInt(min);
    const to = parseInt(max);
    if (from === fromYear && to === toYear) return;
    setFromYear(from);
    setToYear(to);
    const dataLength = to - from + 1;
    setRegimeSelectors(buildDefaultRegimeSelectors(dataLength, regimeCount));
  };

  const handleRegimeCommit = () => {
    if (usePyScorecard) {
      setRegimeCommitNonce((n) => n + 1);
    }
  };

  const handleAllocCommit = (id) => {
    if (!usePyScorecard) return;
    userOverrodeAllocRef.current[id] = true;
    pendingUserAllocFetchRef.current = true;
    setAllocCommitNonce((n) => n + 1);
  };

  const updateRegime = (value, index, justPlot = false, multiplier = 100) => {
    let cartoonBins = [];
    let rs = regimeSelectors;
    if (!justPlot) {
      let pos = index.split("_")[2];
      let buffer = [...regimeSelectors];
      buffer[pos] = value;
      setRegimeSelectors(buffer);
      rs = buffer;
    }
    let data = dataBar?.datasets[0]?.data; // values
    let dataLabels = dataBar?.labels; // years
    let yearRange = [];
    // regime chart
    let newResponse = {
      labels: [],
      datasets: [
        {
          label: "Num of years",
          data: [],
          backgroundColor: "rgba(53, 162, 235, 0.5)",
        },
      ],
    };
    for (let i = 1; i < rs.length; i++) {
      const start = rs[i - 1] - 1;
      const end = rs[i] - 1;
      let holderArray = dataLabels.slice(start, end);
      if (i == rs.length - 1) {
        holderArray = dataLabels.slice(start, end + 1);
      }
      yearRange.push(holderArray);
      cartoonBins.push([parseFloat(data[start]), parseFloat(data[end])]);
      newResponse.labels.push(
        parseFloat(data[start]?.toFixed(2)) +
          " to " +
          parseFloat(data[end]?.toFixed(2))
      );
      newResponse.datasets[0].data.push({
        y: holderArray.length,
      });
    }
    setYearsList((prevValue) => yearRange);
    setRegimeBar(newResponse);
    let newResNoCrash = JSON.parse(JSON.stringify(newResponse));
    newResNoCrash.datasets[0].data[0]["y"] = 0;
    setRegimeBarForNocrash(newResNoCrash);
    setBins((prevValue) => cartoonBins);
  };

  const generateDiceMatrixForOneTwentyFaceDice = (yearsList) => {
    let initialSPXReturn = [...firstAssetValues].sort((a, b) => a[0] - b[0]);

    let minFace;
    let maxFace;
    for (let i = 0; i < initialSPXReturn.length; i++) {
      if (initialSPXReturn[i][0] === fromYear) {
        minFace = i; // Return the index when the year is found
      }
      if (initialSPXReturn[i][0] === toYear) {
        maxFace = i; // Return the index when the year is found
      }
    }
    if (minFace === undefined || maxFace === undefined) return;
    let profitLossVal = new Map();
    for (let j = minFace; j <= maxFace; j++) {
      profitLossVal.set(j + 1, initialSPXReturn[j][1]);
    }
    minFace = 1 + minFace;
    maxFace = 1 + maxFace;

    const matrix = [];

    for (let i = 0; i < selectedValuePath; i++) {
      const row = [];
      for (let j = 0; j < selectedValueFlip; j++) {
        const randomFace =
          Math.floor(Math.random() * (maxFace - minFace + 1)) + minFace; // Random face number <= type
        row.push(randomFace);
      }
      matrix.push(row);
    }
    let yearMatrix = calYearMatrix(initialSPXReturn, matrix);
    const matrixData = calculateWealthMatrixForOneTwenty(matrix, profitLossVal);
    const wealthMatrixData = matrixData[0];
    const profitLossData = matrixData[1];
    setBaseAssetMatrixReturns((prevValue) => profitLossData);
    let percentileValuesForLastCol = calculatePercentilePaths(
      wealthMatrixData,
      selectedValueFlip,
      selectedValuePath
    );
    updateTable(
      percentileValuesForLastCol,
      selectedValueFlip,
      wealthMatrixData,
      profitLossData
    );
    // Initialize the result array with empty arrays for each regime
    const resultArray = new Array(yearsList.length).fill([]).map(() => []);
    // Iterate through yearsMatrix and categorize years into the resultArray
    for (let i = 0; i < yearMatrix.length; i++) {
      for (let j = 0; j < yearMatrix[i].length; j++) {
        yearsList.forEach((regime, index) => {
          if (regime.includes(yearMatrix[i][j])) {
            resultArray[index].push(yearMatrix[i][j]);
          }
        });
      }
    }
    let avgRegime = [];
    let maxValRegime = [];
    let minValRegime = [];
    let resultArraySpxReturns = calReturn(resultArray, initialSPXReturn);
    for (let i = 0; i < resultArraySpxReturns.length; i++) {
      let getAvg = avgCalculate(resultArraySpxReturns[i]);
      avgRegime.push(getAvg);
      let getMax = maxCalc(resultArraySpxReturns[i]);
      maxValRegime.push(getMax);
      let getMin = minCalc(resultArraySpxReturns[i]);
      minValRegime.push(getMin);
    }

    setMinMaxAvgArray({
      minArray: minValRegime,
      maxArray: maxValRegime,
      averageArray: avgRegime,
    });
    setYearMatrixData(yearMatrix);
    return [yearMatrix, yearsList];
  };

  const generateDiceMatrixForOneTwentyFaceDiceForNoCrash = (yearsList) => {
    //if (yearsList[0].length != 0) {
    let initialSPXReturn = [...firstAssetValues].sort((a, b) => a[0] - b[0]);

    let minFace;
    let maxFace;
    let facesExceptFirstRegime = [];
    for (let i = 0; i < initialSPXReturn.length; i++) {
      if (initialSPXReturn[i][0] === fromYear) {
        minFace = i; // Return the index when the year is found
      }
      if (initialSPXReturn[i][0] === toYear) {
        maxFace = i; // Return the index when the year is found
      }
    }
    if (minFace === undefined || maxFace === undefined) return;
    let profitLossVal = new Map();
    for (let j = minFace; j <= maxFace; j++) {
      profitLossVal.set(j + 1, initialSPXReturn[j][1]);
      if (!yearsList[0].includes(initialSPXReturn[j][0])) {
        facesExceptFirstRegime.push(j + 1);
      }
    }
    const matrix = Array.from({ length: selectedValuePath }, () =>
      Array.from(
        { length: selectedValueFlip },
        () =>
          facesExceptFirstRegime[
            Math.floor(Math.random() * facesExceptFirstRegime.length)
          ]
      )
    );
    let yearMatrix = calYearMatrix(initialSPXReturn, matrix);
    const matrixData = calculateWealthMatrixForOneTwenty(matrix, profitLossVal);
    const wealthMatrixData = matrixData[0];
    const profitLossData = matrixData[1];
    setBaseAssetMatrixReturnsNoCrash((prevValue) => profitLossData);
    let percentileValuesForLastCol = calculatePercentilePaths(
      wealthMatrixData,
      selectedValueFlip,
      selectedValuePath
    );
    updateTableForNocrash(
      percentileValuesForLastCol,
      selectedValueFlip,
      wealthMatrixData,
      profitLossData
    );
    // Initialize the result array with empty arrays for each regime
    const resultArray = new Array(yearsList.length).fill([]).map(() => []);
    // Iterate through yearsMatrix and categorize years into the resultArray
    for (let i = 0; i < yearMatrix.length; i++) {
      for (let j = 0; j < yearMatrix[i].length; j++) {
        yearsList.forEach((regime, index) => {
          if (regime.includes(yearMatrix[i][j])) {
            resultArray[index].push(yearMatrix[i][j]);
          }
        });
      }
    }
    let avgRegime = [0];
    let maxValRegime = [0];
    let minValRegime = [0];
    let resultArraySpxReturns = calReturn(resultArray, initialSPXReturn);
    for (let i = 1; i < resultArraySpxReturns.length; i++) {
      let getAvg = avgCalculate(resultArraySpxReturns[i]);
      avgRegime.push(getAvg);
      let getMax = maxCalc(resultArraySpxReturns[i]);
      maxValRegime.push(getMax);
      let getMin = minCalc(resultArraySpxReturns[i]);
      minValRegime.push(getMin);
    }
    setMinMaxAvgArrayNoCrash({
      minArrayNocrash: minValRegime,
      maxArrayNocrash: maxValRegime,
      averageArrayNocrash: avgRegime,
    });

    setYearMatrixDataNocrash(yearMatrix);
    return [yearMatrix, yearsList];
  };

  const calSecondAsset = (yearMatrix, yearsList, id) => {
    if (yearMatrix.length == 0 && yearMatrixData.length != 0) {
      yearMatrix = yearMatrixData;
    }
    let binMatrix = createBinMatrix(yearMatrix, yearsList);
    let assetBin = filterRegimeYears(yearsList, secondAssetValues[id]);
    let newAssetYearMatrix = fillNewMatrix(binMatrix, assetBin);
    const resultAssetArray = new Array(assetBin.length).fill([]).map(() => []);
    // Iterate through yearsMatrix and categorize years into the resultArray
    for (let i = 0; i < newAssetYearMatrix.length; i++) {
      for (let j = 0; j < newAssetYearMatrix[i].length; j++) {
        assetBin.forEach((regime, index) => {
          if (regime.includes(newAssetYearMatrix[i][j])) {
            resultAssetArray[index].push(newAssetYearMatrix[i][j]);
          }
        });
      }
    }

    let avgAssetRegime = [];
    let maxAssetValRegime = [];
    let minAssetValRegime = [];
    let resultArrayReturns = calReturn(resultAssetArray, secondAssetValues[id]);

    for (let i = 0; i < resultArrayReturns.length; i++) {
      let getAvg = avgCalculate(resultArrayReturns[i]);
      avgAssetRegime.push(getAvg);
      let getMax = maxCalc(resultArrayReturns[i]);
      maxAssetValRegime.push(getMax);
      let getMin = minCalc(resultArrayReturns[i]);
      minAssetValRegime.push(getMin);
    }

    setMinAssetArray((prevValue) => ({
      ...prevValue,
      [id]: minAssetValRegime,
    }));
    setMaxAssetArray((prevValue) => ({
      ...prevValue,
      [id]: maxAssetValRegime,
    }));
    setAverageAssetArray((prevValue) => ({
      ...prevValue,
      [id]: avgAssetRegime,
    }));

    //for mix calculation
    let pnlValues = generatePNLValues(secondAssetValues[id]);
    const matrixData = calculateWealthMatrixForOneTwenty(
      newAssetYearMatrix,
      pnlValues,
      "secondAsset"
    );
    let wealthMatrixData = matrixData[0];
    let profitLossData = matrixData[1];
    setOtherAssetMatrixReturns((prevValue) => ({
      ...prevValue,
      [id]: profitLossData,
    }));
    let percentileValuesForLastCol = calculatePercentilePaths(
      wealthMatrixData,
      selectedValueFlip,
      selectedValuePath
    );
    if (
      percentileValuesForLastCol != undefined &&
      percentileValuesForLastCol.length > 1 &&
      wealthMatrixData.length > 1 &&
      profitLossData.length > 1
    ) {
      const AmGm = calAmGm(
        percentileValuesForLastCol,
        selectedValueFlip,
        wealthMatrixData,
        profitLossData
      );
      setAmMean((prevValue) => ({ ...prevValue, [id]: AmGm.am }));
    }
  };

  const calSecondAssetForNocrash = (yearMatrix, yearsList, item) => {
    if (yearMatrix.length == 0 && yearMatrixData.length != 0) {
      yearMatrix = yearMatrixData;
    }
    let binMatrix = createBinMatrix(yearMatrix, yearsList);
    let assetBin = filterRegimeYears(yearsList, secondAssetValues[item]);
    let newAssetYearMatrix = fillNewMatrix(binMatrix, assetBin);
    const resultAssetArray = new Array(assetBin.length).fill([]).map(() => []);
    // Iterate through yearsMatrix and categorize years into the resultArray
    for (let i = 0; i < newAssetYearMatrix.length; i++) {
      for (let j = 0; j < newAssetYearMatrix[i].length; j++) {
        assetBin.forEach((regime, index) => {
          if (regime.includes(newAssetYearMatrix[i][j])) {
            resultAssetArray[index].push(newAssetYearMatrix[i][j]);
          }
        });
      }
    }
    let avgAssetRegime = [0];
    let maxAssetValRegime = [0];
    let minAssetValRegime = [0];
    let resultArrayReturns = calReturn(
      resultAssetArray,
      secondAssetValues[item]
    );

    for (let i = 1; i < resultArrayReturns.length; i++) {
      let getAvg = avgCalculate(resultArrayReturns[i]);
      avgAssetRegime.push(getAvg);
      let getMax = maxCalc(resultArrayReturns[i]);
      maxAssetValRegime.push(getMax);
      let getMin = minCalc(resultArrayReturns[i]);
      minAssetValRegime.push(getMin);
    }

    setMinAssetArrayNocrash((prevValue) => ({
      ...prevValue,
      [item]: minAssetValRegime,
    }));
    setMaxAssetArrayNocrash((prevValue) => ({
      ...prevValue,
      [item]: maxAssetValRegime,
    }));
    setAverageAssetArrayNocrash((prevValue) => ({
      ...prevValue,
      [item]: avgAssetRegime,
    }));

    let pnlValues = generatePNLValues(secondAssetValues[item]);
    const matrixData = calculateWealthMatrixForOneTwenty(
      newAssetYearMatrix,
      pnlValues,
      "secondAsset"
    );
    let wealthMatrixData = matrixData[0];
    let profitLossData = matrixData[1];
    setOtherAssetMatrixReturnsNoCrash((prevValue) => ({
      ...prevValue,
      [item]: profitLossData,
    }));
    let percentileValuesForLastCol = calculatePercentilePaths(
      wealthMatrixData,
      selectedValueFlip,
      selectedValuePath
    );
    if (
      percentileValuesForLastCol != undefined &&
      percentileValuesForLastCol.length > 1 &&
      wealthMatrixData.length > 1 &&
      profitLossData.length > 1
    ) {
      const AmGm = calAmGm(
        percentileValuesForLastCol,
        selectedValueFlip,
        wealthMatrixData,
        profitLossData
      );
      setAmMeanNocrash((prevValue) => ({ ...prevValue, [item]: AmGm.am }));
    }
  };

  const calSecondAssetForReshuffle = (yearMatrix, yearsList, id) => {
    if (yearMatrix.length == 0 && yearMatrixData.length != 0) {
      yearMatrix = yearMatrixData;
    }
    let binMatrix = createBinMatrix(yearMatrix, yearsList);
    let assetBin = filterRegimeYears(yearsList, secondAssetValues[id]);
    let newAssetYearMatrix = fillNewMatrix(binMatrix, assetBin);
    const shuffledMatrix = shuffleMatrixRows(newAssetYearMatrix);

    const resultAssetArray = calReshuffledBin(
      shuffledMatrix,
      binMatrix,
      assetBin
    );
    let avgAssetRegime = [];
    let maxAssetValRegime = [];
    let minAssetValRegime = [];
    let resultArrayReturns = calReturn(resultAssetArray, secondAssetValues[id]);

    for (let i = 0; i < resultArrayReturns.length; i++) {
      let getAvg = avgCalculate(resultArrayReturns[i]);
      avgAssetRegime.push(getAvg);
      let getMax = maxCalc(resultArrayReturns[i]);
      maxAssetValRegime.push(getMax);
      let getMin = minCalc(resultArrayReturns[i]);
      minAssetValRegime.push(getMin);
    }
    setMinAssetReshuffleArray((prevValue) => ({
      ...prevValue,
      [id]: minAssetValRegime,
    }));
    setMaxAssetReshuffleArray((prevValue) => ({
      ...prevValue,
      [id]: maxAssetValRegime,
    }));
    setAverageAssetReshuffleArray((prevValue) => ({
      ...prevValue,
      [id]: avgAssetRegime,
    }));

    //for mix calculation
    let pnlValues = generatePNLValues(secondAssetValues[id]);
    const matrixData = calculateWealthMatrixForOneTwenty(
      shuffledMatrix,
      pnlValues,
      "secondAssetReshuffle"
    );
    let wealthMatrixData = matrixData[0];
    let profitLossData = matrixData[1];
    setOtherAssetMatrixReshuffleReturns((prevValue) => ({
      ...prevValue,
      [id]: profitLossData,
    }));
    let percentileValuesForLastCol = calculatePercentilePaths(
      wealthMatrixData,
      selectedValueFlip,
      selectedValuePath
    );
    if (
      percentileValuesForLastCol != undefined &&
      percentileValuesForLastCol.length > 1 &&
      wealthMatrixData.length > 1 &&
      profitLossData.length > 1
    ) {
      const AmGm = calAmGm(
        percentileValuesForLastCol,
        selectedValueFlip,
        wealthMatrixData,
        profitLossData
      );
      setAmMeanReshuffle(AmGm.am);
    }
  };

  function calReshuffledBin(shuffledMatrix, binMatrix, assetBin) {
    const newBin = new Array(assetBin.length).fill([]).map(() => []);
    // Populate newBin matrix based on binMatrix and yearsMatrix
    for (let i = 0; i < shuffledMatrix.length; i++) {
      for (let j = 0; j < shuffledMatrix[i].length; j++) {
        let binValue = binMatrix[i][j];
        let yearValue = shuffledMatrix[i][j];
        newBin[binValue].push(yearValue);
      }
    }
    return newBin;
  }

  function shuffleMatrixRows(matrix) {
    return matrix.map((row) => {
      const shuffledRow = [...row];

      for (let i = shuffledRow.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffledRow[i], shuffledRow[j]] = [shuffledRow[j], shuffledRow[i]];
      }

      return shuffledRow;
    });
  }

  function fillNewMatrix(binMatrix, assetbinMatrix) {
    return binMatrix.map((row) =>
      row.map((binIndex) => {
        const availableYears = assetbinMatrix[binIndex];
        return availableYears[
          Math.floor(Math.random() * availableYears.length)
        ];
      })
    );
  }

  function filterRegimeYears(regimeMatrix, yearAssets = []) {
    return regimeMatrix.map((regimeYears) =>
      regimeYears.filter((year) => {
        //change 1 to id
        for (const asset of yearAssets) {
          // for (const asset of yearAssets[1]) {
          if (asset[0] === year) {
            return true;
          }
        }
        return false;
      })
    );
  }

  function createBinMatrix(yearsMatrix, regimeMatrix) {
    const newMatrix = Array.from({ length: yearsMatrix.length }, () =>
      Array(yearsMatrix[0].length).fill(0)
    );
    for (let i = 0; i < yearsMatrix.length; i++) {
      for (let j = 0; j < yearsMatrix[0].length; j++) {
        const currentYear = yearsMatrix[i][j];

        for (
          let regimeIndex = 0;
          regimeIndex < regimeMatrix.length;
          regimeIndex++
        ) {
          if (regimeMatrix[regimeIndex].includes(currentYear)) {
            newMatrix[i][j] = regimeIndex;
            break;
          }
        }
      }
    }

    return newMatrix;
  }

  const calReturn = (resultArray, initialSPXReturn) => {
    let spxRet = [];
    for (let i = 0; i < resultArray.length; i++) {
      let newRow = [];
      for (let j = 0; j < resultArray[i].length; j++) {
        if (Array.isArray(initialSPXReturn)) {
          initialSPXReturn.forEach((val) => {
            if (val[0] == resultArray[i][j]) {
              newRow.push(val[1]);
            }
          });
        }
      }
      spxRet.push(newRow);
    }
    return spxRet;
  };

  const calYearMatrix = (initialSPXReturn, matrix) => {
    let yearMatrixArr = [];
    for (let i = 0; i < matrix.length; i++) {
      let newRow = [];
      for (let j = 0; j < matrix[i].length; j++) {
        newRow.push(initialSPXReturn[matrix[i][j] - 1][0]);
      }
      yearMatrixArr.push(newRow);
    }
    return yearMatrixArr;
  };

  const calculatePercentilePaths = (
    wealthMatrix,
    selectedValueFlip,
    selectedValuePath
  ) => {
    if (wealthMatrix.length > 1) {
      const lastColumn = wealthMatrix.map((row) => row[selectedValueFlip - 1]); // Get the last column (end wealth)

      // Create an array of objects with row numbers and their corresponding end wealth
      const rowsWithWealth = lastColumn.map((wealth, index) => ({
        wealth,
        row: index,
      }));

      // Sort the rows based on the end wealth
      rowsWithWealth.sort((a, b) => a.wealth - b.wealth);
      // Calculate percentile paths based on the sorted end wealth
      const percentileValues = [];
      const percentiles = [10, 20, 30, 40, 50, 60, 70, 80, 90];
      percentileValues.push(rowsWithWealth[0].row);
      for (let i = 0; i < percentiles.length; i++) {
        const index = Math.floor((percentiles[i] / 100) * selectedValuePath);
        const percentilePath = rowsWithWealth[index - 1].row;
        percentileValues.push(percentilePath);
      }
      percentileValues.push(rowsWithWealth[rowsWithWealth.length - 1].row);
      return percentileValues;
    }
  };

  const calculateWealthMatrixForOneTwenty = (matrix, profitLossVal, type) => {
    const wealthMatrix = [];
    const profitLossMatrix = [];

    if (profitLossVal != undefined && profitLossVal.length != 0) {
      for (let row = 0; row < matrix.length; row++) {
        const newRow = [];
        const profitLossRow = [];
        let wealth = 1; // Initial wealth at the start of calculation for each row

        for (let col = 0; col < matrix[row].length; col++) {
          let faceNo = matrix[row][col];
          let faceNoProfitLossVal;
          if (profitLossVal.has(faceNo)) {
            faceNoProfitLossVal = profitLossVal.get(faceNo);
          }
          let profitLossPercentage = faceNoProfitLossVal; // faceNo is 1-based index, array index is 0-based
          profitLossRow.push(profitLossPercentage);
          let profitLossPercentageWithoutSign = Math.abs(profitLossPercentage);
          // Calculate wealth for each column in the row based on the profit/loss percentage
          let profitLossCalc = wealth;
          profitLossCalc *= profitLossPercentageWithoutSign;
          if (profitLossPercentage < 0) {
            wealth -= profitLossCalc;
          } else {
            wealth += profitLossCalc;
          }
          newRow.push(wealth);
        }

        wealthMatrix.push(newRow);
        profitLossMatrix.push(profitLossRow);
      }
    }
    return [wealthMatrix, profitLossMatrix];
  };

  const updateTable = (
    percentileValuesForLastCol,
    selectedValueFlip,
    wealthMatrixData,
    profitLossData
  ) => {
    let endWealths = lastColumnValue(
      percentileValuesForLastCol,
      wealthMatrixData
    );
    let cagrData = cagrCal(
      percentileValuesForLastCol,
      wealthMatrixData,
      selectedValueFlip
    );
    const matrixWithOnesColumn = wealthMatrixData.map((row) => [1, ...row]);
    let ddData = ddDataCal(percentileValuesForLastCol, matrixWithOnesColumn);
    let amData = amDataCal(percentileValuesForLastCol, profitLossData);

    let amDataAvg = avgCalc(amData);
    let gmCal = cagrData[5];
    setAm(amDataAvg);
    setGm(gmCal);
  };

  const updateTableForNocrash = (
    percentileValuesForLastCol,
    selectedValueFlip,
    wealthMatrixData,
    profitLossData
  ) => {
    let endWealths = lastColumnValue(
      percentileValuesForLastCol,
      wealthMatrixData
    );
    let cagrData = cagrCal(
      percentileValuesForLastCol,
      wealthMatrixData,
      selectedValueFlip
    );
    const matrixWithOnesColumn = wealthMatrixData.map((row) => [1, ...row]);
    let ddData = ddDataCal(percentileValuesForLastCol, matrixWithOnesColumn);
    let amData = amDataCal(percentileValuesForLastCol, profitLossData);

    let amDataAvg = avgCalc(amData);
    let gmCal = cagrData[5];
    setAmNocrash(amDataAvg);
    setGmNocrash(gmCal);
  };

  const lastColumnValue = (percentileValuesForLastCol, wealthMatrixData) => {
    const endColumnData = [];
    percentileValuesForLastCol.forEach((rowNo) => {
      if (wealthMatrixData[rowNo]) {
        const rowData = wealthMatrixData[rowNo];
        const endColumnValue = rowData[rowData.length - 1]; // Fetching the last element of each row
        endColumnData.push(endColumnValue.toFixed(2));
      }
    });
    return endColumnData;
  };

  const amDataCal = (percentileValuesForLastCol, profitLossData) => {
    const amDataArr = [];
    percentileValuesForLastCol.forEach((rowNo) => {
      if (profitLossData[rowNo]) {
        const rowData = profitLossData[rowNo];
        const amRowData = avgCal(rowData); // Fetching the last element of each row
        amDataArr.push(amRowData);
      }
    });
    return amDataArr;
  };

  const avgCal = (rowData) => {
    let sum = 0;
    for (let i = 0; i < rowData.length; i++) {
      sum += rowData[i];
    }
    const avg = sum / rowData.length;
    return (avg * 100).toFixed(1);
  };

  const maxCalc = (rowData) => {
    let max = -Infinity;
    for (let i = 0; i < rowData.length; i++) {
      max = Math.max(rowData[i], max);
    }
    return (max * 100).toFixed(2);
  };

  const minCalc = (rowData) => {
    let min = Infinity;
    for (let i = 0; i < rowData.length; i++) {
      min = Math.min(rowData[i], min);
    }
    return (min * 100).toFixed(2);
  };

  const avgCalc = (rowData) => {
    let sum = 0;
    for (let i = 0; i < rowData.length; i++) {
      sum += Number(rowData[i]);
    }
    const avg = sum / rowData.length;
    return avg.toFixed(2);
  };

  const avgCalculate = (rowData) => {
    let sum = 0;
    for (let i = 0; i < rowData.length; i++) {
      sum += rowData[i];
    }
    const avg = sum / rowData.length;
    return (avg * 100).toFixed(2);
  };

  const ddDataCal = (percentileValuesForLastCol, wealthMatrixData) => {
    const endColumnDd = [];
    percentileValuesForLastCol.forEach((rowNo) => {
      if (wealthMatrixData[rowNo]) {
        const rowData = wealthMatrixData[rowNo];
        const endColumnValue = maxDrawDown(rowData); // Fetching the last element of each row
        endColumnDd.push(endColumnValue);
      }
    });
    return endColumnDd;
  };

  const maxDrawDown = (wealthArr) => {
    const dd = [];
    for (let i = 1; i <= wealthArr.length; i++) {
      dd.push(wealthArr[i - 1] / Math.max(...wealthArr.slice(0, i)) - 1);
    }
    return (Math.min(...dd) * 100).toFixed(1);
  };

  const cagrCal = (
    percentileValuesForLastCol,
    wealthMatrixData,
    selectedValueFlip
  ) => {
    const resultArray = [];
    if (percentileValuesForLastCol !== undefined) {
      percentileValuesForLastCol.forEach((rowNo) => {
        if (wealthMatrixData[rowNo]) {
          const rowVal = wealthMatrixData[rowNo];
          const endColValue = rowVal[rowVal.length - 1]; // Fetching the last element of each row
          const modifiedValue =
            Math.pow(endColValue, 1 / selectedValueFlip) - 1;
          const newModifiedNo = (modifiedValue * 100).toFixed(1);
          resultArray.push(newModifiedNo);
        }
      });
    }
    return resultArray;
  };

  const handleDefault = () => {
    setSelectedType("normal");
  };

  const handleReshuffle = () => {
    setSelectedType("reshuffle");
    if (usePyScorecard) return;
    calAssetsMixReshuffle(yearMatrixData, yearsList);
  };

  const handleNocrash = () => {
    setSelectedType("nocrash");
    if (usePyScorecard) return;
    calAssetMixNoCrash();
  };

  // /* *
  //  * Developer Notes :
  //  *  SPX is called as First Asset
  //  *    db record for SPX utilized by the API for fetching data is 'spx' and is stored in firstAsset Variable
  //  *    the applilication uses firstAssetName to display the name as 'SPX'
  //  *    the value of SPX in each year from 1901 to 2023 is fetched and stored in FirstAssetValues to minimize the API calls later
  //  *    the returns of SPX are calculated by using a common helper function and stored in FirstAssetReturns
  //  *    yearList contains the years in each regime
  //  *    regimeSelectors contain the index of the regime boundary
  //  *    also the dataBar is a ready chart option similar as to the first chart of OSH
  //  * */

  const handleAllocSliderChange = (event, id) => {
    setAlloc((prevValue) => ({
      ...prevValue,
      [id]: {
        firstAssetAlloc: 100 - event.target.value,
        secondAssetAlloc: event.target.value,
      },
    }));
  };

  useEffect(() => {
    if (usePyScorecard) return;
    assets.map((item, idx) => {
      if (selectedType === "reshuffle") {
        calAssetsMixReshuffle(yearMatrixData, yearsList, item);
      }
    });
  }, [
    firstAssetValues,
    secondAssetValues,
    fromYear,
    toYear,
    alloc,
    yearsList,
    yearMatrixData,
    regimeCount,
    regimeSelectors,
    baseAssetMatrixRetruns,
    otherAssetMatrixReshuffleReturns,
    assets,
    selectedType,
    usePyScorecard,
  ]);

  useEffect(() => {
    if (usePyScorecard) return;
    assets.map((items, idx) => {
      if (selectedType === "nocrash") {
        calAssetMixNoCrash(items);
      }
    });
  }, [
    firstAssetValues,
    secondAssetValues,
    secondAssetName,
    fromYear,
    toYear,
    alloc,
    yearsList,
    yearMatrixDataNocrash,
    regimeCount,
    regimeSelectors,
    baseAssetMatrixReturnsNoCrash,
    otherAssetMatrixReturnsNoCrash,
    selectedType,
    assets,
    usePyScorecard,
  ]);

  const calAssetsMixReshuffle = (yearMatrix, yearsList, item) => {
    if (
      baseAssetMatrixRetruns.length > 1 &&
      otherAssetMatrixReshuffleReturns[item] !== undefined &&
      otherAssetMatrixReshuffleReturns[item].length > 1
    ) {
      // 25 X 1000 matrix returns
      const mixMatrix = generateMix(
        baseAssetMatrixRetruns,
        otherAssetMatrixReshuffleReturns[item],
        alloc[item]["secondAssetAlloc"]
      );
      //bin mapping based on base asset -spc years matrix
      let binMatrix = createBinMatrix(yearMatrixData, yearsList);
      //categorizing 25 x 1000 retruns based on bin Matrix
      let mappedBin = binMapping(binMatrix, mixMatrix);

      let avgAssetRegime = [];
      let maxAssetValRegime = [];
      let minAssetValRegime = [];
      for (let bin in mappedBin) {
        let getAvg = avgCalculate(mappedBin[bin]);
        avgAssetRegime.push(getAvg);
        let getMax = maxCalc(mappedBin[bin]);
        maxAssetValRegime.push(getMax);
        let getMin = minCalc(mappedBin[bin]);
        minAssetValRegime.push(getMin);
      }

      setMixMinAssetReshuffleArray((prevValue) => ({
        ...prevValue,
        [item]: minAssetValRegime,
      }));
      setMixMaxAssetReshuffleArray((prevValue) => ({
        ...prevValue,
        [item]: maxAssetValRegime,
      }));
      setMixAvgAssetReshuffleArray((prevValue) => ({
        ...prevValue,
        [item]: avgAssetRegime,
      }));

      const wealthMatrix = generateMixReturnsWealth(mixMatrix);
      let percentileValuesForLastCol = calculatePercentilePaths(
        wealthMatrix,
        selectedValueFlip,
        selectedValuePath
      );
      const AmGm = calAmGm(
        percentileValuesForLastCol,
        selectedValueFlip,
        wealthMatrix,
        mixMatrix
      );
      setMixAMReshuffle((prevValue) => ({ ...prevValue, [item]: AmGm.am }));
      setMixGMReshuffle((prevValue) => ({ ...prevValue, [item]: AmGm.gm }));
    }
  };

  const generatePNLValues = (assetReturns) => {
    if (Array.isArray(assetReturns)) {
      let initialSPXReturn = [...assetReturns].sort((a, b) => a[0] - b[0]);
      let profitLossVal = new Map();

      for (let i = 0; i < initialSPXReturn.length; i++) {
        profitLossVal.set(initialSPXReturn[i][0], initialSPXReturn[i][1]);
      }

      return profitLossVal;
    }
  };

  // // Chart 5 Logic
  const handlePercentileDropdown = (event, percentileName, id) => {
    switch (percentileName) {
      case "percentile_1":
        lock
          ? setPercentile1Dropdown((prevValue) => ({
              ...prevValue,
              2: event.target.value,
              3: event.target.value,
              4: event.target.value,
              5: event.target.value,
              [id]: event.target.value,
            }))
          : setPercentile1Dropdown((prevValue) => ({
              ...prevValue,
              [id]: event.target.value,
            }));
        break;
      case "percentile_2":
        lock
          ? setPercentile2Dropdown((prevValue) => ({
              ...prevValue,
              2: event.target.value,
              3: event.target.value,
              4: event.target.value,
              5: event.target.value,
              [id]: event.target.value,
            }))
          : setPercentile2Dropdown((prevValue) => ({
              ...prevValue,
              [id]: event.target.value,
            }));
        break;
    }
  };

  //MixGeneration new Logic
  useEffect(() => {
    if (usePyScorecard) return;
    assets.map((item, idx) => {
      if (
        baseAssetMatrixRetruns.length > 1 &&
        otherAssetMatrixRetruns[item] !== undefined &&
        otherAssetMatrixRetruns[item].length > 1
      ) {
        // 25 X 1000 matrix returns
        const mixMatrix = generateMix(
          baseAssetMatrixRetruns,
          otherAssetMatrixRetruns[item],
          alloc[item]["secondAssetAlloc"]
        );
        //bin mapping based on base asset -spc years matrix
        let binMatrix = createBinMatrix(yearMatrixData, yearsList);
        //categorizing 25 x 1000 retruns based on bin Matrix
        let mappedBin = binMapping(binMatrix, mixMatrix);

        let avgAssetRegime = [];
        let maxAssetValRegime = [];
        let minAssetValRegime = [];
        for (let bin in mappedBin) {
          let getAvg = avgCalculate(mappedBin[bin]);
          avgAssetRegime.push(getAvg);
          let getMax = maxCalc(mappedBin[bin]);
          maxAssetValRegime.push(getMax);
          let getMin = minCalc(mappedBin[bin]);
          minAssetValRegime.push(getMin);
        }

        setMixMinAssetArray((prevValue) => ({
          ...prevValue,
          [item]: minAssetValRegime,
        }));
        setMixMaxAssetArray((prevValue) => ({
          ...prevValue,
          [item]: maxAssetValRegime,
        }));
        setMixAvgAssetArray((prevValue) => ({
          ...prevValue,
          [item]: avgAssetRegime,
        }));

        // when selected asset (other asset ) method for calculating am

        const wealthMatrix = generateMixReturnsWealth(mixMatrix);
        let percentileValuesForLastCol = calculatePercentilePaths(
          wealthMatrix,
          selectedValueFlip,
          selectedValuePath
        );
        const AmGm = calAmGm(
          percentileValuesForLastCol,
          selectedValueFlip,
          wealthMatrix,
          mixMatrix
        );
        setMixAM((prevValue) => ({ ...prevValue, [item]: AmGm.am }));
        setMixGM((prevValue) => ({ ...prevValue, [item]: AmGm.gm }));
      }
    });
  }, [
    baseAssetMatrixRetruns,
    otherAssetMatrixRetruns,
    alloc,
    averageArray,
    assets,
    usePyScorecard,
  ]);

  const calAmGm = (
    percentileValuesForLastCol,
    selectedValueFlip,
    wealthMatrixData,
    profitLossData
  ) => {
    let endWealths = lastColumnValue(
      percentileValuesForLastCol,
      wealthMatrixData
    );
    let cagrData = cagrCal(
      percentileValuesForLastCol,
      wealthMatrixData,
      selectedValueFlip
    );
    const matrixWithOnesColumn = wealthMatrixData.map((row) => [1, ...row]);
    let ddData = ddDataCal(percentileValuesForLastCol, matrixWithOnesColumn);
    let amData = amDataCal(percentileValuesForLastCol, profitLossData);

    let amDataAvg = avgCalc(amData);
    let gmCal = cagrData[5];
    return { am: amDataAvg, gm: gmCal };
  };

  // //NoCrashMix
  const calAssetMixNoCrash = (item) => {
    if (
      baseAssetMatrixReturnsNoCrash.length > 1 &&
      otherAssetMatrixReturnsNoCrash[item] !== undefined &&
      otherAssetMatrixReturnsNoCrash[item].length > 1
    ) {
      // 25 X 1000 matrix returns
      const mixMatrix = generateMix(
        baseAssetMatrixReturnsNoCrash,
        otherAssetMatrixReturnsNoCrash[item],
        alloc[item]["secondAssetAlloc"]
      );
      //bin mapping based on base asset -spc years matrix
      let binMatrix = createBinMatrix(yearMatrixDataNocrash, yearsList);
      //categorizing 25 x 1000 retruns based on bin Matrix
      let mappedBin = binMapping(binMatrix, mixMatrix);

      let avgAssetRegime = [0];
      let maxAssetValRegime = [0];
      let minAssetValRegime = [0];
      for (let bin in mappedBin) {
        let getAvg = avgCalculate(mappedBin[bin]);
        avgAssetRegime.push(getAvg);
        let getMax = maxCalc(mappedBin[bin]);
        maxAssetValRegime.push(getMax);
        let getMin = minCalc(mappedBin[bin]);
        minAssetValRegime.push(getMin);
      }

      setMinMixNoCrash((prevValue) => ({
        ...prevValue,
        [item]: minAssetValRegime,
      }));
      setMaxMixNoCrash((prevValue) => ({
        ...prevValue,
        [item]: maxAssetValRegime,
      }));
      setAvgMixNoCrash((prevValue) => ({
        ...prevValue,
        [item]: avgAssetRegime,
      }));

      // when selected asset (other asset ) method for calculating am
      const wealthMatrix = generateMixReturnsWealth(mixMatrix);
      let percentileValuesForLastCol = calculatePercentilePaths(
        wealthMatrix,
        selectedValueFlip,
        selectedValuePath
      );
      const AmGm = calAmGm(
        percentileValuesForLastCol,
        selectedValueFlip,
        wealthMatrix,
        mixMatrix
      );
      setAmMixNoCrash((prevValue) => ({ ...prevValue, [item]: AmGm.am }));
      setGmMixNoCrash((prevValue) => ({ ...prevValue, [item]: AmGm.gm }));
    }
  };

  //MULTI Asset --------------------------------------------------------------------------------------------

  useEffect(() => {
    mulitiCEAplotCoordinates(mixAM, mixGM, am, gm);
  }, [
    mixAM,
    mixGM,
    am,
    gm,
    amNocrash,
    gmNocrash,
    amMixNoCrash,
    gmMixNoCrash,
    mixAMReshuffle,
    mixGMReshuffle,
  ]);

  useEffect(() => {
    if (lock === true) {
      setPercentile1Dropdown({
        1: percentile1Dropdown[1],
        2: percentile1Dropdown[1],
        3: percentile1Dropdown[1],
        4: percentile1Dropdown[1],
        5: percentile1Dropdown[1],
      });
      setPercentile2Dropdown({
        1: percentile2Dropdown[1],
        2: percentile2Dropdown[1],
        3: percentile2Dropdown[1],
        4: percentile2Dropdown[1],
        5: percentile2Dropdown[1],
      });
    }
  }, [lock]);

  const handleAddAsset = () => {
    let newAssetId = assets.length + 1;
    if (assets.length <= 5) {
      setActiveAsset(newAssetId);
      setSecondAsset((prevValue) => {
        let lastKey = Object.keys(prevValue)[Object.keys(prevValue).length - 1];
        let lastKeyValue = prevValue[lastKey];

        return { ...prevValue, [newAssetId]: lastKeyValue };
      });
      setSecondAssetName((prevValue) => {
        let lastKey = Object.keys(prevValue)[Object.keys(prevValue).length - 1];
        let lastKeyValue = prevValue[lastKey];

        return { ...prevValue, [newAssetId]: lastKeyValue };
      });
      setSecondAssetValues((prevValue) => {
        let lastKey = Object.keys(prevValue)[Object.keys(prevValue).length - 1];
        let lastKeyValue = prevValue[lastKey];

        return { ...prevValue, [newAssetId]: lastKeyValue };
      });
      if (allocLock) {
        setAlloc((prevValue) => {
          return {
            ...prevValue,
            [newAssetId]: {
              firstAssetAlloc: prevValue[newAssetId - 1]["firstAssetAlloc"],
              secondAssetAlloc: prevValue[newAssetId - 1]["secondAssetAlloc"],
            },
          };
        });
      }
    }

    setAssets((prevValue) => {
      let length = prevValue.length;
      if (length < 5) {
        return [...prevValue, prevValue[length - 1] + 1];
      }
      return prevValue;
    });
  };

  const intitialSecondAssetValues = async (options, id) => {
    const apiValues = await callApiBasic(options);
    setSecondAssetValues((prevValue) => ({ ...prevValue, [id]: apiValues }));
  };

  const callApiBasic = async (options) => {
    try {
      setLoading(true);
      const data = await axios.post(`${api_url}/fetch-data`, options);
      let raw_data = (data && data.data.data) || [];
      setLoading(false);
      return raw_data;
    } catch (error) {
      setLoading(false);
    }
  };

  const handleLock = () => {
    setLock((prevValue) => !prevValue);
  };

  const handleAllocLock = () => {
    setAllocLock((prevValue) => !prevValue);
  };

  useEffect(() => {
    if (!allocLock) {
      // Snap to recommendedAlloc unless the user overrode that column's slider.
      setAlloc((prevValue) => {
        let changed = false;
        const newAlloc = { ...prevValue };
        Object.keys(prevValue).forEach((key) => {
          if (userOverrodeAllocRef.current[key]) return;
          if (typeof percentile2DataMaxAllocation[key] === "number") {
            const rec = percentile2DataMaxAllocation[key];
            if (prevValue[key]?.secondAssetAlloc !== rec) {
              changed = true;
              newAlloc[key] = {
                firstAssetAlloc: 100 - rec,
                secondAssetAlloc: rec,
              };
            }
          }
        });
        return changed ? newAlloc : prevValue;
      });
    }
  }, [allocLock, percentile2DataMaxAllocation]);

  const handlePin = () => {
    setPin((prevValue) => !prevValue);
  };

  const mulitiCEAplotCoordinates = (mixAM, mixGM, am, gm) => {
    let coordinates = [];
    if (Object.keys(mixAM).length === Object.keys(mixGM).length) {
      for (let i = 1; i <= Object.keys(mixAM).length; i++) {
        coordinates.push({
          asset: secondAssetName[i],
          xAxis: (-(mixAM[i] - am)).toFixed(1),
          yAxis: (-(mixGM[i] - gm)).toFixed(1),
          borderColor:
            assetsLineBgColor[secondAssetName[i]] || "rgba(141, 130, 205, 0.6)",
        });
      }
    }

    return coordinates;
  };

  //MULTI Asset --------------------------------------------------------------------------------------------

  //Custom SLider logic
  const handleInsuranceSliderValue = (event, id) => {
    setInsuranceSliderValue((prevValue) => ({
      ...prevValue,
      [id]: event.target.value,
    }));
    let newValue = parseInt(event.target.value);
    if (newValue <= 60) {
      newValue = (0.1 + (newValue / 60) * (10.0 - 0.1)).toFixed(1);
    } else if (newValue >= 61 && newValue <= 100) {
      newValue = Math.round(
        11 + ((newValue - 61) / (100 - 61)) * (100 - 11)
      ).toFixed(0);
    }
    setAlloc((prevValue) => ({
      ...prevValue,
      [id]: { firstAssetAlloc: 100 - newValue, secondAssetAlloc: newValue },
    }));
  };

  const getAmGMData = (items) => {
    return {
      normal: {
        am: mixAM[items] != undefined ? mixAM[items] - am : 0,
        gm: mixGM[items] != undefined ? mixGM[items] - gm : 0,
      },
      reshuffle: {
        am: mixAMReshuffle[items] != undefined ? mixAMReshuffle[items] - am : 0,
        gm: mixGMReshuffle[items] != undefined ? mixGMReshuffle[items] - gm : 0,
      },
      nocrash: {
        am:
          amMixNoCrash[items] != undefined
            ? amMixNoCrash[items] - amNocrash
            : 0,
        gm:
          gmMixNoCrash[items] != undefined
            ? gmMixNoCrash[items] - gmNocrash
            : 0,
      },
    };
  };

  return (
    <div className="bg-slate-200 py-5 text-sm text-gray-700 mx-auto w-full max-w-[1630px] relative px-4">
      <div className="flex flex-col-reverse lg:flex-row">
      <button
        className={clsx(
          "mt-1 md:mt-0 border-2 border-[#69a4ce] rounded-md h-8 w-8 p-1.5",
          !pin && "bg-[#69a4ce] pinned"
        )}
        onClick={handlePin}
        aria-label="Pin assets"
        aria-pressed={!pin}
        data-testid="main-multi-pin-toggle-btn"
      >
          <svg
            stroke="currentColor"
            fill="currentColor"
            stroke-width="0"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M7.886 1.553a1.75 1.75 0 0 1 2.869.604l.633 1.629a5.667 5.667 0 0 0 3.725 3.395l3.959 1.131a1.749 1.749 0 0 1 .757 2.92L16.06 15l5.594 5.595a.749.749 0 0 1-.326 1.275.749.749 0 0 1-.734-.215L15 16.061l-3.768 3.768a1.749 1.749 0 0 1-2.92-.757l-1.131-3.96a5.665 5.665 0 0 0-3.395-3.724l-1.63-.633a1.75 1.75 0 0 1-.603-2.869Zm6.589 12.912 4.293-4.294a.25.25 0 0 0-.108-.417L14.7 8.623A7.165 7.165 0 0 1 9.99 4.33L9.357 2.7a.25.25 0 0 0-.41-.086L2.614 8.946a.25.25 0 0 0 .086.41l1.63.634a7.167 7.167 0 0 1 4.294 4.71l1.13 3.96a.25.25 0 0 0 .417.108l4.294-4.293Z"></path>
          </svg>
        </button>
      <RangeSelector
        handleYearChange={handleYearChange}
        regimeCount={regimeCount}
        setRegimeCount={setRegimeCount}
        regimeSelectors={regimeSelectors}
        dataBar={dataBar}
        updateRegime={updateRegime}
        onRegimeCommit={handleRegimeCommit}
        multiAssetToggle={multiAssetToggle}
        handleMultiAssetToggle={handleMultiAssetToggle}
      />
      </div>
      
      <div className="flex flex-col-reverse md:flex-row justify-center mb-5 relative items-center gap-x-5">
      <BtnGroup
          buttons={[
            { value: "normal", label: "Default", onClick: handleDefault },
            {
              value: "reshuffle",
              label: "Reshuffle",
              onClick: handleReshuffle,
            },
            { value: "nocrash", label: "No Crash", onClick: handleNocrash },
          ]}
          selectedValue={selectedType}
          testIdPrefix="main-multi-type"
        />

          <button
            className="ms-auto -mt-8 md:mt-0 mb-3 md:mb-0 md:ms-0 bg-[#69a4ce] border-2 border-[#69a4ce] rounded-md w-24 p-[6px] text-white"
            onClick={triggerRerender}
            aria-label="Reset"
            data-testid="main-multi-reset-btn"
          >
            Reset
          </button>
     
      </div>
      <div className="relative pt-12 lg:pt-0 min-h-[320px]">
      <Loader loading={loading} contained />
      <div
        className={`${loading ? "pointer-events-none" : ""}`}
        aria-busy={loading}
      >
      <div className="flex items-center lg:grid grid-cols-5 gap-3 2xl:gap-5 ps-24 lg:ps-28 overflow-x-auto">
        {assets.map((items, idx) => {
          return (
            <div className="w-full max-w-[300px] lg:max-w-full" key={idx}>
              <div className="relative">
                {pin ? (
                  <div className="bg-white rounded-lg shadow-xl shadow-slate-300 p-2">
                    <div className="flex flex-row justify-center w-full h-auto gap-1 text-sm 2xl:text-base">
                      {!isEmpty(regimeBar) &&
                        !isEmpty(regimeBar.datasets[0]) &&
                        regimeBar.datasets[0].data.map((dataValue, idx) => (
                          <div
                            className="flex flex-1 flex-col bg-white h-12 w-12 justify-center items-center border rounded"
                            key={idx}
                          >
                            {dataValue.y}
                          </div>
                        ))}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-xl shadow-slate-300 p-2">
                    {(selectedType == "normal" ||
                      selectedType == "reshuffle") && (
                      <BarCandles
                        dataset={regimeBar}
                        title={{
                          x: "Annual " + firstAssetName + " Returns",
                          y: "Num of Years",
                        }}
                      />
                    )}
                    {selectedType == "nocrash" && (
                      <BarCandles
                        dataset={regimeBarForNocrash}
                        title={{
                          x: "Annual " + firstAssetName + " Returns",
                          y: "Num of Years",
                        }}
                      />
                    )}
                  </div>
                )}

                {items === 1 && (
                  <div className="absolute right-full me-3 lg:me-4 text-right text-sm top-1/2 -translate-y-1/2">
                    SPX
                    <br /> Distribution
                  </div>
                )}
              </div>
              <div className="grid grid-cols-12 my-3 relative text-sm 2xl:text-base">
                {items === 1 && (
                  <div className="absolute right-full me-3 lg:me-4 text-right top-1/2 -translate-y-1/2">
                    Bootstrap
                  </div>
                )}
                <div className="col-span-12">
                  <hr class="border-1 border-black" />
                </div>
              </div>
              <div className="mb-4 relative">
                {items === 1 && (
                  <div className="absolute right-full me-3 lg:me-4 text-right text-sm top-1/2 -translate-y-1/2">
                    SPX
                  </div>
                )}
                <div className="">
                  {pin && (
                    <>
                      <div className="bg-white rounded-lg shadow-xl shadow-slate-300 p-2">
                        <div className="flex flex-row justify-center w-full h-auto gap-1 text-sm 2xl:text-base">
                          {!isEmpty(averageArray) &&
                            averageArray.map((dataValue, idx) => {
                              return (
                                <>
                                  <div className="flex flex-1 flex-col bg-white h-12 w-12 justify-center items-center border rounded">
                                    {dataValue}
                                  </div>
                                </>
                              );
                            })}
                        </div>
                      </div>
                    </>
                  )}
                  {!pin && (
                    <>
                      <div className="bg-white rounded-lg shadow-xl shadow-slate-300 p-2">
                        {(selectedType == "normal" ||
                          selectedType == "reshuffle") && (
                          <BarChart2
                            min={minArray}
                            max={maxArray}
                            average={averageArray}
                          />
                        )}
                        {selectedType == "nocrash" && (
                          <BarChart2
                            min={minArrayNocrash}
                            max={maxArrayNocrash}
                            average={averageArrayNocrash}
                          />
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="mb-4 relative">
                <div className="">
                  {pin && (
                    <>
                      <div className="bg-white rounded-lg shadow-xl shadow-slate-300 p-2">
                        <div className="flex flex-row justify-center w-full h-auto gap-1 text-sm 2xl:text-base">
                          {!isEmpty(averageAssetArray[items]) &&
                            averageAssetArray[items].map((dataValue, idx) => (
                              <div
                                className="flex flex-1 flex-col bg-white h-12 w-12 justify-center items-center border rounded"
                                key={idx}
                              >
                                {!isEmpty(dataValue) ? dataValue : "X"}
                              </div>
                            ))}
                        </div>
                      </div>
                    </>
                  )}
                  {!pin && (
                    <>
                      <div className="w-full bg-white rounded-lg shadow-xl shadow-slate-300 p-2">
                        {selectedType == "normal" && (
                          <BarChart3
                            min={minAssetArray[items] || []}
                            max={maxAssetArray[items] || []}
                            average={averageAssetArray[items] || []}
                          />
                        )}
                        {selectedType == "reshuffle" && (
                          <BarChart3
                            min={minAssetReshuffleArray[items] || []}
                            max={maxAssetReshuffleArray[items] || []}
                            average={averageAssetReshuffleArray[items] || []}
                          />
                        )}
                        {selectedType == "nocrash" && (
                          <BarChart3
                            min={minAssetArrayNocrash[items] || []}
                            max={maxAssetArrayNocrash[items] || []}
                            average={averageAssetArrayNocrash[items] || []}
                          />
                        )}
                      </div>
                    </>
                  )}
                  {items === 1 && (
                    <div className="absolute right-full me-3 lg:me-4 text-right text-sm top-1/2 -translate-y-1/2">
                      Other
                      <br /> Asset
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-12 my-2">
                <div className="col-span-12 mb-2">
                  <hr class="border-1 border-black" />
                </div>
                <div className="col-span-12">
                  <hr class="border-1 border-black" />
                </div>
              </div>
              <div className="mb-4 relative">
                <div className="">
                  {pin && (
                    <>
                      <div className="bg-white rounded-lg shadow-xl shadow-slate-300 p-2 relative">
                        <div className="flex flex-row justify-center w-full h-auto gap-1 relative text-sm 2xl:text-base">
                          {!isEmpty(mixAvgAssetArray[items]) &&
                            mixAvgAssetArray[items].map((dataValue, idx) => (
                              <div className="flex flex-1 flex-col bg-white h-12 w-12 justify-center items-center border rounded">
                                {dataValue}
                              </div>
                            ))}
                          
                        </div>
                        {items === 1 && (
                            <div className="whitespace-nowrap absolute right-full me-3 lg:me-4 text-right text-sm top-1/2 -translate-y-1/2">
                              SPX +<br /> Other Asset
                            </div>
                          )}
                      </div>
                    </>
                  )}
                  {!pin && (
                    <>
                      <div className="relative">
                        <div className="flex flex-col mx-auto w-full bg-white rounded-lg shadow-xl shadow-slate-300 p-2">
                          {selectedType == "normal" && (
                            <BarChart4
                              min={mixMinAssetArray[items] || []}
                              max={mixMaxAssetArray[items] || []}
                              average={mixAvgAssetArray[items] || []}
                            />
                          )}
                          {selectedType == "reshuffle" && (
                            <BarChart4
                              min={mixMinAssetReshuffleArray[items] || []}
                              max={mixMaxAssetReshuffleArray[items] || []}
                              average={mixAvgAssetReshuffleArray[items] || []}
                            />
                          )}
                          {selectedType == "nocrash" && (
                            <BarChart4
                              min={minMixNoCrash[items] || []}
                              max={maxMixNoCrash[items] || []}
                              average={avgMixNoCrash[items] || []}
                            />
                          )}
                        </div>
                        {items === 1 && (
                          <div className="absolute right-full me-4 text-right w-full text-sm top-1/2 -translate-y-1/2">
                            SPX +<br /> Other Asset
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className="col-span-5 mt-2 relative">
                  <div className="col-span-5 flex gap-2 my-2">
                    <div className="text-sm w-full">
                      <CatListDropdown
                        id={generateUniqueId()}
                        // label={"Other Asset"}
                        value={secondAsset[items]}
                        options={secondAssetDropdown}
                        onChange={() => handleSecondAsset(event, items)}
                        data-testid={`main-multi-asset-${items}-second-asset-select`} />
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-3 mb-2">
                    <div className="w-full flex items-center gap-2 mt-2 relative">
                      {items === 1 &&
                        <button
                          className='bg-[#e9e9ed] h-8 w-8 rounded-md border absolute left-[-38px] flex items-center justify-center'
                          onClick={handleAllocLock}
                          aria-pressed={Boolean(allocLock)}
                          aria-label="Lock allocations"
                          data-testid="main-multi-alloc-lock-toggle-btn">
                          {allocLock ? (
                            <SvgLock height={20} width={20} />
                          ) : (
                            <SvgUnlock height={20} width={20} />
                          )}
                        </button>
                      }
                      {insuranceTypes.some((type) =>
                        secondAsset[items].includes(type)
                      ) ? (
                        <CustomSlider
                          disabled={allocLock}
                          min={0}
                          max={100}
                          sliderValue={insuranceSliderValue[items]}
                          displayValue={alloc[items]["secondAssetAlloc"]}
                          step={0 <= 60 ? 0.1 : 1}
                          onChange={() =>
                            handleInsuranceSliderValue(event, items)
                          }
                          onMouseUp={() => handleAllocCommit(items)}
                          onTouchEnd={() => handleAllocCommit(items)}
                          data-testid={`main-multi-asset-${items}-insurance-alloc-input:range`} />
                      ) : (
                        <Slider
                          disabled={allocLock}
                          min={0}
                          max={100}
                          onChange={() => handleAllocSliderChange(event, items)}
                          onMouseUp={() => handleAllocCommit(items)}
                          onTouchEnd={() => handleAllocCommit(items)}
                          disabledBin={false}
                          value={alloc[items]["secondAssetAlloc"]}
                          data-testid={`main-multi-asset-${items}-alloc-input:range`} />
                      )}
                    </div>

                    <div className="flex mx-auto">
                      <div className="whitespace-nowrap text-sm">
                        {alloc[items]["firstAssetAlloc"]}% SPX +{" "}
                        {alloc[items]["secondAssetAlloc"]}%{" "}
                        {secondAssetName[items]}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 relative">
                    {items === 1 && (
                      <div className="absolute right-full me-3 lg:me-4 text-right text-sm top-1/2 -translate-y-1/2">
                        Difference
                        <br /> V/S SPX
                      </div>
                    )}

                    <div>
                      <div className="flex gap-2">
                        <span className="text-xs bg-white whitespace-nowrap text-gray-700 font-semibold py-2 px-2 border-gray-400 rounded-lg shadow-lg shadow-slate-300">
                          {`AM: ${parseFloat(getAmGMData(items)?.[selectedType].am).toFixed(1)}%`}
                        </span>
                        <span className="text-xs bg-white whitespace-nowrap text-gray-700 font-semibold py-2 px-2 border-gray-400 rounded-lg shadow-lg shadow-slate-300">
                          {`GM: ${parseFloat(getAmGMData(items)?.[selectedType].gm).toFixed(1)}%`}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 text-center">
                        <p className="text-xs p-1 whitespace-nowrap  font-semibold">
                          COST
                        </p>
                        <p className="text-xs p-1 whitespace-nowrap  font-semibold">
                          NET
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-4 space-y-2">
                <div className="w-full h-[220px] bg-white rounded-lg shadow-xl shadow-slate-300 p-2">
                  <Chart5
                  maintainAspectRatio={false}
                    assetType={isInsurance(secondAsset[items])}
                    fromYear={fromYear}
                    toYear={toYear}
                    firstAssetValues={firstAssetValues}
                    secondAssetValues={secondAssetValues[items] || []}
                    yearsMatrix={yearMatrixData}
                    yearsList={yearsList}
                    percentile1Dropdown={percentile1Dropdown[items]}
                    percentile2Dropdown={percentile2Dropdown[items]}
                    //new mix logic props
                    baseAssetMatrixRetruns={baseAssetMatrixRetruns}
                    otherAssetMatrixRetruns={
                      otherAssetMatrixRetruns[items] || []
                    }
                    reshuffle={selectedType == "reshuffle"}
                    normal={selectedType == "normal"}
                    otherAssetMatrixReshuffleReturns={
                      otherAssetMatrixReshuffleReturns[items] || []
                    }
                    nocrash={selectedType == "nocrash"}
                    baseAssetMatrixReturnsNoCrash={
                      baseAssetMatrixReturnsNoCrash || []
                    }
                    otherAssetMatrixReturnsNoCrash={
                      otherAssetMatrixReturnsNoCrash[items] || []
                    }
                    setPercentile2DataMaxAllocation={(e) =>
                      handleSetPercentile2DataMaxAllocation(e, items)
                    }
                    precomputedPercentile1={
                      usePyScorecard
                        ? chart5ByMode?.[items]?.[selectedType]?.percentile1
                        : null
                    }
                    precomputedPercentile2={
                      usePyScorecard
                        ? chart5ByMode?.[items]?.[selectedType]?.percentile2
                        : null
                    }
                  />
                </div>
                <div className="flex gap-2">
                  <CatListDropdown
                    id={"percentile_1"}
                    value={percentile1Dropdown[items]}
                    options={percentileOptions0To100}
                    onChange={() =>
                      handlePercentileDropdown(event, "percentile_1", items)
                    }
                    disabled={items > 1 && lock ? true : false}
                    data-testid={`main-multi-asset-${items}-percentile-1-select`} />
                  <CatListDropdown
                    id={"percentile_2"}
                    value={percentile2Dropdown[items]}
                    options={percentileOptions5To95}
                    onChange={() =>
                      handlePercentileDropdown(event, "percentile_2", items)
                    }
                    disabled={Boolean(items > 1 && lock)}
                    data-testid={`main-multi-asset-${items}-percentile-2-select`} />
                  {items === 1 && (
                    <button
                      className="bg-white h-8 min-w-8 rounded-md border flex items-center justify-center"
                      onClick={handleLock}
                      aria-pressed={Boolean(lock)}
                      aria-label="Lock percentiles"
                      data-testid="main-multi-percentile-lock-toggle-btn">
                      {lock ? (
                        <SvgLock height={20} width={20} />
                      ) : (
                        <SvgUnlock height={20} width={20} />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {assets.length !== 5 && (
          <div className="absolute top-0 right-0 lg:static h-10 w-10">
            <button
              className="border-none bg-transparent"
              onClick={handleAddAsset}
              aria-label="Add asset"
              data-testid="main-multi-add-asset-btn"
            >
              <svg
                width="38"
                height="38"
                viewBox="0 0 38 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="19" cy="19" r="19" fill="#69A4CE" />
                <rect x="18" y="10" width="3" height="18" fill="white" />
                <rect
                  x="10"
                  y="21"
                  width="3"
                  height="19"
                  transform="rotate(-90 10 21)"
                  fill="white"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
      </div>
      </div>
      <div className="flex flex-row max-w-5xl mx-auto">
        <div className="flex flex-col mx-auto w-full bg-white rounded-lg shadow-xl p-2">
          {selectedType == "normal" && (
            <AllocCEAplot
              ref={allocCEAplotRef}
              // xAxis={(-(mixAM - am)).toFixed(1)}
              // yAxis={(-(mixGM - gm)).toFixed(1)}
              isMulti
              coordinates={mulitiCEAplotCoordinates(mixAM, mixGM, am, gm)}
              xAxis={(-(mixAM - am)).toFixed(1)}
              yAxis={(-(mixGM - gm)).toFixed(1)}
            />
          )}
          {selectedType == "reshuffle" && (
            <AllocCEAplot
              ref={allocCEAplotRef}
              isMulti
              coordinates={mulitiCEAplotCoordinates(
                mixAMReshuffle,
                mixGMReshuffle,
                am,
                gm
              )}
              xAxis={(-(mixAMReshuffle - am)).toFixed(1)}
              yAxis={(-(mixGMReshuffle - gm)).toFixed(1)}
            />
          )}
          {selectedType == "nocrash" && (
            <AllocCEAplot
              ref={allocCEAplotRef}
              isMulti
              coordinates={mulitiCEAplotCoordinates(
                amMixNoCrash,
                gmMixNoCrash,
                amNocrash,
                gmNocrash
              )}
              xAxis={(-(amMixNoCrash - amNocrash)).toFixed(1)}
              yAxis={(-(gmMixNoCrash - gmNocrash)).toFixed(1)}
            />
          )}
        </div>
      </div>
    </div>
  );
};
export default MultiAsset;
